import { tv, VariantProps } from "@heroui/react";
import { AriaToastProps, ToastAria, useToast } from "@react-aria/toast";
import { ToastState } from "@react-stately/toast";
import { DOMProps } from "@react-types/shared";
import { NotificationType, OmitRef } from "core/types";
import { Button, ButtonProps } from "ds/ui";
import {
  CheckCircleIcon,
  CircleSlashIcon,
  InfoIcon,
  XIcon,
} from "lucide-react";
import { ElementType, useRef } from "react";
import { useTranslations } from "translations";

export type ToastNotificationProps<T> = AriaToastProps<T> & {
  state: ToastState<T>;
};

type NotificationVariant = VariantProps<typeof notificationVariants>;
type NotificationColor = Exclude<NotificationVariant["color"], undefined>;

type BaseNotificationContentType = DOMProps & {
  blockDuplicateNotificationTypes?: boolean;
  color: NotificationColor;
  message: string;
  onClose?: () => void;
  title: string;
  type: NotificationType;
};

type WithActions = {
  actionLeft: OmitRef<ButtonProps<false>>;
  actionRight: OmitRef<ButtonProps<false>>;
};

export type NotificationContentType =
  | (BaseNotificationContentType & Partial<WithActions>)
  | (BaseNotificationContentType & WithActions);

const notificationVariants = tv({
  defaultVariants: {
    color: "primary",
  },
  slots: {
    icon: "h-5 w-5",
    wrapperBorder:
      "sm:hidden flex h-max w-[320px] max-w-[320px] rounded-lg  shadow-md justify-end",
  },
  variants: {
    color: {
      danger: {
        icon: "text-danger",
        wrapperBorder: "bg-danger",
      },
      primary: {
        icon: "text-primary",
        wrapperBorder: "bg-primary",
      },
      success: {
        icon: "text-success",
        wrapperBorder: "bg-success",
      },
      warning: {
        icon: "text-warning",
        wrapperBorder: "bg-warning",
      },
    },
  },
});

const ICONS: Record<NotificationColor, ElementType> = {
  danger: CircleSlashIcon,
  primary: InfoIcon,
  success: CheckCircleIcon,
  warning: InfoIcon,
};

export type NotificationContentProps =
  ToastNotificationProps<NotificationContentType> &
    Pick<ToastAria, "closeButtonProps" | "titleProps">;

export const NotificationContent = ({
  closeButtonProps,
  state,
  titleProps,
  toast: {
    content: { actionLeft, actionRight, color, message, onClose, title, type },
    key,
  },
}: NotificationContentProps) => {
  const translations = useTranslations();
  const { icon, wrapperBorder } = notificationVariants({
    color,
  });

  const Icon = ICONS[color];

  return (
    <div
      className={wrapperBorder()}
      data-testid={`notification-${type}-${key}`}
    >
      <div className="flex min-h-[132px] w-[98%] rounded-l-sm rounded-r-lg bg-white pl-3 pr-4 text-small">
        <div className="flex w-[13%] py-4">
          <Icon className={icon()} />
        </div>
        <div className="flex h-full w-[77%] flex-col justify-between py-4">
          <div className="flex flex-col">
            <h2 className="pb-2 font-semibold" {...titleProps}>
              {title}
            </h2>
            <p className="text-gray-600" {...titleProps}>
              {message}
            </p>
          </div>
          <div className="flex pt-6">
            {actionLeft && (
              <Button
                color="primary"
                size="sm"
                variant="faded"
                {...actionLeft}
                onPress={(e) => {
                  actionLeft.onPress?.(e);
                  state.close(key);
                }}
              >
                {actionLeft.title}
              </Button>
            )}
            {actionRight && (
              <Button
                className="ml-4"
                color="primary"
                size="sm"
                variant="faded"
                {...actionRight}
                onPress={(e) => {
                  actionRight.onPress?.(e);
                  state.close(key);
                }}
              >
                {actionRight.title}
              </Button>
            )}
          </div>
        </div>
        <div className="flex w-[10%] py-3">
          <Button
            {...closeButtonProps}
            aria-label={translations.actions.close}
            color="black"
            id="close-notification"
            isIconOnly
            size="xs"
            variant="light"
            onPress={() => {
              onClose?.();
              state.close(key);
            }}
          >
            <XIcon className="h-5 w-5 [&>svg]:!stroke-lg" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const Notification = ({
  state,
  ...props
}: ToastNotificationProps<NotificationContentType>) => {
  const ref = useRef(null);
  const { closeButtonProps, contentProps, titleProps, toastProps } = useToast(
    props,
    state,
    ref,
  );

  return (
    <div ref={ref} {...toastProps}>
      <div {...contentProps}>
        <NotificationContent
          closeButtonProps={closeButtonProps}
          state={state}
          titleProps={titleProps}
          toast={props.toast}
        />
      </div>
    </div>
  );
};

Notification.displayName = "RecareUI.Notification";
